import { useMemo, useState } from "react";
import {
  Button,
  Col,
  Container,
  FloatingLabel,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { isMobile } from "react-device-detect";
import axiosAPI from "../../../axiosAPI";
import {
  FINANCIAL_REPORT_TYPES,
  getFinancialReportMeta,
  useFinancialReport,
} from "../../../hooks/useFinancialReport";
import { exportFinancialReportExcel } from "./excel/exportFinancialReport";
import { FinancialReportDocument } from "./pdf/documents";

function defaultPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const pad = (n) => String(n).padStart(2, "0");
  const from = `${year}-${pad(month + 1)}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const to = `${year}-${pad(month + 1)}-${pad(lastDay)}`;
  return { from, to };
}

function Relatorios() {
  const initial = useMemo(() => defaultPeriod(), []);
  const [reportType, setReportType] = useState(FINANCIAL_REPORT_TYPES[0].value);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [modalShow, setModalShow] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);

  const {
    data,
    isLoading,
    isFetching,
    isError,
  } = useFinancialReport(activeRequest?.type, activeRequest?.filters, {
    enabled: modalShow && Boolean(activeRequest),
  });

  const meta = getFinancialReportMeta(activeRequest?.type || reportType);
  const ready = Boolean(data) && !isLoading && !isFetching;

  const returnFileNamePDF = () =>
    `relatorio-${meta.filename}-${new Date().getTime()}.pdf`;

  const validatePeriod = () => {
    if (!from || !to) return false;
    if (from > to) {
      window.alert("A data inicial deve ser anterior ou igual à data final.");
      return false;
    }
    return true;
  };

  const handleGenerate = () => {
    if (!validatePeriod()) return;
    setActiveRequest({
      type: reportType,
      filters: { from, to },
    });
    setModalShow(true);
  };

  const handleExportExcel = async () => {
    if (!validatePeriod()) return;
    setExportingExcel(true);
    try {
      const params = new URLSearchParams({ from, to });
      const { data: reportData } = await axiosAPI.get(
        `/financial/reports/${reportType}?${params.toString()}`
      );
      exportFinancialReportExcel(reportType, reportData);
    } catch {
      window.alert("Não foi possível exportar o Excel. Tente novamente.");
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportExcelFromModal = () => {
    if (!data || !activeRequest?.type) return;
    exportFinancialReportExcel(activeRequest.type, data);
  };

  const handleClose = () => {
    setModalShow(false);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-0">Relatórios</h5>
          <small className="text-muted">
            Selecione o tipo e o período para gerar PDF ou Excel.
          </small>
        </div>
      </div>

      <Row className="g-3 mb-3">
        <Col md={3}>
          <FloatingLabel controlId="reportType" label="Tipo de relatório">
            <Form.Select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              {FINANCIAL_REPORT_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel controlId="reportFrom" label="Data início">
            <Form.Control
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel controlId="reportTo" label="Data fim">
            <Form.Control
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md={5} className="financial-report-actions">
          <Button
            type="button"
            className="btn-bg-primary text-dark borderColor"
            onClick={handleGenerate}
            disabled={!from || !to}
          >
            <i className="bi bi-file-earmark-pdf" /> Gerar PDF
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={handleExportExcel}
            disabled={!from || !to || exportingExcel}
          >
            {exportingExcel ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-1"
                />
                Exportando…
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-excel" /> Exportar Excel
              </>
            )}
          </Button>
        </Col>
      </Row>

      <Modal
        show={modalShow}
        onHide={handleClose}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton className="modalGradient">
          <Modal.Title className="text-white">
            <i className="bi bi-file-earmark-pdf" /> {meta.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading || isFetching ? (
            <div className="loader-spinner text-center py-5">
              <Spinner animation="border" variant="warning" />
            </div>
          ) : isError ? (
            <p className="text-danger mb-0">
              Não foi possível carregar o relatório. Tente novamente.
            </p>
          ) : ready ? (
            <Container>
              {isMobile ? (
                <Row className="flex-column gap-2">
                  <PDFDownloadLink
                    document={
                      <FinancialReportDocument
                        type={activeRequest.type}
                        data={data}
                      />
                    }
                    fileName={returnFileNamePDF()}
                  >
                    {({ loading }) =>
                      loading
                        ? "Preparando PDF…"
                        : `Download: ${returnFileNamePDF()}`
                    }
                  </PDFDownloadLink>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleExportExcelFromModal}
                  >
                    <i className="bi bi-file-earmark-excel" /> Exportar Excel
                  </Button>
                </Row>
              ) : (
                <Row>
                  <Col>
                    <div className="mb-2 d-flex gap-2 flex-wrap">
                      <PDFDownloadLink
                        document={
                          <FinancialReportDocument
                            type={activeRequest.type}
                            data={data}
                          />
                        }
                        fileName={returnFileNamePDF()}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        {({ loading }) =>
                          loading ? "Preparando…" : "Baixar PDF"
                        }
                      </PDFDownloadLink>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        onClick={handleExportExcelFromModal}
                      >
                        <i className="bi bi-file-earmark-excel" /> Exportar Excel
                      </Button>
                    </div>
                    <PDFViewer width="100%" height="900px" title={meta.label}>
                      <FinancialReportDocument
                        type={activeRequest.type}
                        data={data}
                      />
                    </PDFViewer>
                  </Col>
                </Row>
              )}
            </Container>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Relatorios;
